import React,{useEffect,useState}from"react";
import{Box,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TablePagination,TableRow,TextField,Typography,useMediaQuery}from"@mui/material";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import TreasuryModals from"./AdminTreasury/TreasuryModals";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const API_BASE="/api/treasury";

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

function money(value){
  return Number(value||0).toFixed(2);
}

function errorMessage(data,fallback){
  const value=data?.error||data?.detail;
  if(typeof value==="string")return value;
  if(value&&typeof value==="object")return Object.values(value).flat().join(" ");
  return fallback;
}

function visibleReference(movement){
  const visible=String(movement?.referencia_visible||"").trim();
  if(visible)return visible;
  if(movement?.origen==="SALDO_INICIAL")return"Saldo inicial";
  if(movement?.origen==="TRASPASO")return"Traspaso";
  if(movement?.origen==="CONCILIACION")return"Conciliación";
  if(movement?.origen==="REVERSO")return"Reverso";
  return"-";
}

export default function AdminTreasury(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError}=useAlert();
  const isMobile=useMediaQuery("(max-width:900px)");
  const token=getToken();
  const[summary,setSummary]=useState(null);
  const[movements,setMovements]=useState([]);
  const[loading,setLoading]=useState(false);
  const[page,setPage]=useState(0);
  const[totalItems,setTotalItems]=useState(0);
  const[search,setSearch]=useState("");
  const[typeFilter,setTypeFilter]=useState("");
  const[methodFilter,setMethodFilter]=useState("");
  const[originFilter,setOriginFilter]=useState("");
  const[dateFrom,setDateFrom]=useState("");
  const[dateTo,setDateTo]=useState("");
  const rowsPerPage=isMobile?10:30;
  const canView=userCan(user,"tesoreria.view");
  const canCreate=userCan(user,"tesoreria.create");
  const authHeaders={Authorization:`Bearer ${token}`};

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  const preserveScrollChange=(setter,value)=>{
    const scrollY=window.scrollY;
    setter(value);
    setPage(0);
    requestAnimationFrame(()=>window.scrollTo({top:scrollY,left:0,behavior:"auto"}));
    setTimeout(()=>window.scrollTo({top:scrollY,left:0,behavior:"auto"}),0);
  };

  const loadSummary=async()=>{
    if(!canView)return;
    try{
      const response=await fetch(`${API_BASE}/resumen/`,{headers:authHeaders,credentials:"include"});
      const data=await response.json();
      if(!response.ok){showError(errorMessage(data,"No se pudo cargar Tesorería"));return;}
      setSummary(data);
    }catch{
      showError("Error de conexión");
    }
  };

  const loadMovements=async()=>{
    if(!canView)return;
    try{
      setLoading(true);
      const params=new URLSearchParams();
      params.append("page",String(page+1));
      params.append("page_size",String(rowsPerPage));
      if(search.trim())params.append("q",search.trim());
      if(typeFilter)params.append("tipo",typeFilter);
      if(methodFilter)params.append("medio",methodFilter);
      if(originFilter)params.append("origen",originFilter);
      if(dateFrom)params.append("fecha_desde",dateFrom);
      if(dateTo)params.append("fecha_hasta",dateTo);
      const response=await fetch(`${API_BASE}/movimientos/?${params.toString()}`,{headers:authHeaders,credentials:"include"});
      const data=await response.json();
      if(!response.ok){showError(errorMessage(data,"No se pudieron cargar los movimientos"));return;}
      setMovements(data.results||[]);
      setTotalItems(data.pagination?.total_items||0);
      if(data.pagination?.page&&data.pagination.page-1!==page)setPage(data.pagination.page-1);
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  const reload=async()=>{
    await loadSummary();
    if(page===0)await loadMovements();
    else setPage(0);
  };

  useEffect(()=>{loadSummary();},[canView]);

  useEffect(()=>{
    const delay=setTimeout(()=>{loadMovements();},350);
    return()=>clearTimeout(delay);
  },[page,rowsPerPage,search,typeFilter,methodFilter,originFilter,dateFrom,dateTo,canView]);

  const summaryCardSx={
    p:2.2,
    borderRadius:"14px",
    border:`1px solid ${dashboardTheme.border_color}`,
    bgcolor:dashboardTheme.card_background,
    boxShadow:"none",
    minHeight:105,
    display:"flex",
    flexDirection:"column",
    justifyContent:"center"
  };

  const labelSx={
    fontSize:13,
    fontWeight:600,
    color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#6b7280"
  };

  const valueSx={
    mt:.5,
    fontSize:{xs:22,md:26},
    fontWeight:700,
    color:dashboardTheme.text_color,
    lineHeight:1.15
  };

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Tesorería</Typography>
              <TreasuryModals dashboardTheme={dashboardTheme} canCreate={canCreate} onReload={reload}/>
            </Box>

            {canView&&(
              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,1fr)",xl:"repeat(5,1fr)"},gap:1.5,mb:2}}>
                <Paper elevation={0} sx={summaryCardSx}>
                  <Typography sx={labelSx}>Caja disponible</Typography>
                  <Typography sx={valueSx}>${money(summary?.saldos?.caja)}</Typography>
                </Paper>
                <Paper elevation={0} sx={summaryCardSx}>
                  <Typography sx={labelSx}>Efectivo disponible</Typography>
                  <Typography sx={valueSx}>${money(summary?.saldos?.efectivo)}</Typography>
                </Paper>
                <Paper elevation={0} sx={summaryCardSx}>
                  <Typography sx={labelSx}>Banco disponible</Typography>
                  <Typography sx={valueSx}>${money(summary?.saldos?.banco??summary?.saldos?.transferencia)}</Typography>
                </Paper>
                <Paper elevation={0} sx={summaryCardSx}>
                  <Typography sx={labelSx}>Disponible total</Typography>
                  <Typography sx={valueSx}>${money(summary?.saldos?.total)}</Typography>
                </Paper>
                <Paper elevation={0} sx={summaryCardSx}>
                  <Typography sx={labelSx}>Cuentas por pagar</Typography>
                  <Typography sx={valueSx}>${money(summary?.cuentas_por_pagar?.total)}</Typography>
                  <Typography sx={{mt:.5,fontSize:12.5,color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#6b7280"}}>Compras ${money(summary?.cuentas_por_pagar?.compras_gastos)} · Nómina ${money(summary?.cuentas_por_pagar?.nomina)}</Typography>
                </Paper>
              </Box>
            )}

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"flex",flexDirection:{xs:"column",xl:"row"},gap:1.5,flexWrap:"wrap"}}>
                <TextField fullWidth size="small" label="Buscar por concepto o referencia" value={search} onChange={event=>{setSearch(event.target.value);setPage(0);}} sx={{maxWidth:{xs:"100%",xl:320},...dashboardTheme.inputStyles}}/>

                <TextField
                  size="small"
                  type="date"
                  label="Desde"
                  value={dateFrom}
                  onChange={event=>preserveScrollChange(setDateFrom,event.target.value)}
                  InputLabelProps={{shrink:true}}
                  inputProps={{max:dateTo||undefined}}
                  sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}
                />

                <TextField
                  size="small"
                  type="date"
                  label="Hasta"
                  value={dateTo}
                  onChange={event=>preserveScrollChange(setDateTo,event.target.value)}
                  InputLabelProps={{shrink:true}}
                  inputProps={{min:dateFrom||undefined}}
                  sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}
                />

                <FormControl size="small" sx={{minWidth:{xs:"100%",sm:145},...dashboardTheme.inputStyles}}>
                  <InputLabel>Tipo</InputLabel>
                  <Select label="Tipo" value={typeFilter} onChange={event=>preserveScrollChange(setTypeFilter,event.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="INGRESO">Ingreso</MenuItem>
                    <MenuItem value="EGRESO">Egreso</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}>
                  <InputLabel>Medio</InputLabel>
                  <Select label="Medio" value={methodFilter} onChange={event=>preserveScrollChange(setMethodFilter,event.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="CAJA">Caja</MenuItem>
                    <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                    <MenuItem value="BANCO">Banco</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{minWidth:{xs:"100%",sm:175},...dashboardTheme.inputStyles}}>
                  <InputLabel>Origen</InputLabel>
                  <Select label="Origen" value={originFilter} onChange={event=>preserveScrollChange(setOriginFilter,event.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="VENTA">Venta</MenuItem>
                    <MenuItem value="COMPRA_GASTO">Compra / Gasto</MenuItem>
                    <MenuItem value="NOMINA">Nómina</MenuItem>
                    <MenuItem value="MANUAL">Manual</MenuItem>
                    <MenuItem value="TRASPASO">Traspaso</MenuItem>
                    <MenuItem value="SALDO_INICIAL">Saldo inicial</MenuItem>
                    <MenuItem value="CONCILIACION">Conciliación</MenuItem>
                    <MenuItem value="REVERSO">Reverso</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Tipo</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Medio</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Origen</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:260}}>Concepto</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:170}}>Referencia</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Monto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movements.map(movement=>(
                        <TableRow key={movement.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{movement.fecha||"-"}</TableCell>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{movement.tipo_nombre||movement.tipo}</TableCell>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{movement.medio==="CAJA"?"Caja":movement.medio==="TRANSFERENCIA"||movement.medio==="BANCO"?"Banco":movement.medio_nombre||movement.medio}</TableCell>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{movement.origen_nombre||movement.origen}</TableCell>
                          <TableCell>
                            <Typography sx={{fontSize:14,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>{movement.concepto||"-"}</Typography>
                            {movement.observacion&&<Typography sx={{mt:.3,fontSize:12.5,color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#6b7280"}}>{movement.observacion}</Typography>}
                          </TableCell>
                          <TableCell>{visibleReference(movement)}</TableCell>
                          <TableCell align="right" sx={{whiteSpace:"nowrap",fontWeight:700}}>{movement.tipo==="EGRESO"?"-":"+"}${money(movement.monto)}</TableCell>
                        </TableRow>
                      ))}
                      {!loading&&movements.length===0&&canView&&(
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No hay movimientos registrados</TableCell>
                        </TableRow>
                      )}
                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>Cargando movimientos...</TableCell>
                        </TableRow>
                      )}
                      {!canView&&(
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No tienes permisos para ver Tesorería</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(_event,newPage)=>{
                  const scrollY=window.scrollY;
                  setPage(newPage);
                  requestAnimationFrame(()=>window.scrollTo({top:scrollY,left:0,behavior:"auto"}));
                  setTimeout(()=>window.scrollTo({top:scrollY,left:0,behavior:"auto"}),0);
                }}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}